
import { SenderAvatar } from './LetterAvatar';
import { Box, Button, ChevronDown, ChevronUp, Heading, Tag } from '../../../../../../mads';
import { Text } from '../../../../../../mads/Text';
import { useI18n } from '../../../../../../shared/I18n';
import { useEmployerOutreachInfo } from '../../../../hooks/useEmployerOutreachInfo';
import { ViewJobVariant } from '../ViewJobMainContent/ViewJobMainContent';

import { colors } from '@indeed/ifl-tokens';
import React, { useState } from 'react';
import { StyleSheet } from 'react-native';

interface EmployerOutreachProps {
    jobKey: string;
    variant?: ViewJobVariant;
}

interface EmployerOutreachMessageProps {
    message: string;
    variant?: ViewJobVariant;
}

interface FormattedTimestamp {
    datePrefix: string;
    time: string;
    period: string;
}

const formatMessageTimestamp = (timestamp: number): FormattedTimestamp => {
    const messageDate = new Date(timestamp);
    const today = new Date();

    // Check if the message is from today
    const isToday =
        messageDate.getDate() === today.getDate() &&
        messageDate.getMonth() === today.getMonth() &&
        messageDate.getFullYear() === today.getFullYear();

    const timeString = messageDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });

    // Split time and AM/PM
    const match = timeString.match(/^(\d{1,2}:\d{2})\s*(AM|PM)$/i);
    const time = match ? match[1] : timeString;
    const period = match ? match[2] : '';

    const datePrefix = isToday
        ? 'Today'
        : messageDate.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: messageDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
          });

    return {
        datePrefix,
        time,
        period,
    };
};

export const EmployerOutreach: React.FC<EmployerOutreachProps> = ({
    jobKey,
    variant = ViewJobVariant.Default,
}) => {
    const i18n = useI18n();
    const result = useEmployerOutreachInfo(jobKey);

    if (!result) {
        return null;
    }

    const { mostRecentEngagement } = result;

    const formattedTimestamp = formatMessageTimestamp(
        new Date(mostRecentEngagement.createdDate).getTime(),
    );
    const senderName = mostRecentEngagement.sender.name
        ? mostRecentEngagement.sender.name
        : mostRecentEngagement.job?.company || 'Unknown Sender';

    return (
        <>
            <Box
                sx={{
                    marginBlockStart: 16,
                    ...(variant === ViewJobVariant.FullScreen && styles.outerBorder),
                }}
            >
                <Box sx={{ paddingRight: variant === ViewJobVariant.FullScreen ? 20 : 0 }}>
                    {variant === ViewJobVariant.FullScreen && (
                        <Box sx={{ flexDirection: 'row' }}>
                            <Tag
                                status="success"
                                sx={{
                                    paddingHorizontal: 8,
                                    paddingVertical: 3,
                                }}
                            >
                                {i18n.gettext('New message received')}
                            </Tag>
                        </Box>
                    )}

                    <OutreachMessageCard
                        message={mostRecentEngagement.conversation.initialMessage.textBody ?? ''}
                        variant={variant}
                    />

                    <Box sx={styles.senderInfo}>
                        <SenderAvatar senderName={senderName} />
                        {variant === ViewJobVariant.Default ? (
                            <>
                                <Text sx={styles.senderName}>
                                    {senderName} {'•'}
                                </Text>
                                <Text sx={styles.timestamp}>
                                    {' '}
                                    {formattedTimestamp.datePrefix} {formattedTimestamp.time}{' '}
                                    {formattedTimestamp.period}
                                </Text>
                            </>
                        ) : (
                            <>
                                <Heading level={1} sx={styles.senderNameBold}>
                                    {senderName}
                                </Heading>
                                <Text sx={styles.date} level={1}>
                                    {'  '}
                                    {formattedTimestamp.datePrefix}
                                </Text>
                                <Text sx={styles.time} level={1}>
                                    {' '}
                                    {formattedTimestamp.time}{' '}
                                    {formattedTimestamp.period.toLowerCase()}
                                </Text>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
        </>
    );
};

const OutreachMessageCard: React.FC<EmployerOutreachMessageProps> = ({ message, variant }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { pgettext } = useI18n();
    const MAX_CHARS = 445;
    const needsTruncation = message.length > MAX_CHARS;
    const displayMessage = isExpanded || !needsTruncation ? message : message.slice(0, MAX_CHARS);

    return (
        <Box
            sx={{
                ...styles.messageContainer,
                ...(needsTruncation && { paddingBottom: 0 }),
                backgroundColor: variant === ViewJobVariant.Default ? '#f8f9fa' : colors.background.weak,
            }}
        >
            <Text sx={styles.message}>
                {displayMessage}
                {needsTruncation && !isExpanded && '...'}
            </Text>
            {needsTruncation && (
                <Box
                    sx={{
                        marginTop: 2,
                        alignItems: 'flex-start',
                        marginBottom: 0,
                        paddingBottom: 0,
                    }}
                >
                    <Button
                        variant="text"
                        size="md"
                        onPress={() => setIsExpanded(!isExpanded)}
                        sx={{ padding: 0, marginLeft: 0 }}
                        iconAfter={!isExpanded ? <ChevronDown /> : <ChevronUp />}
                    >
                        {isExpanded
                            ? pgettext('Show less', 'Show less')
                            : pgettext('Show more', 'Show more')}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

const styles = StyleSheet.create({
    outerBorder: {
        borderRadius: 20,
        borderColor: colors.border.weak,
        borderWidth: 1,
        padding: 16,
    },
    messageContainer: {
        display: 'flex',
        padding: 16,
        marginVertical: 8,
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        borderBottomLeftRadius: 4,
        borderBottomRightRadius: 20,
    },
    senderName: {
        fontSize: 11,
        color: 'foreground.strong',
        marginLeft: 4,
        fontWeight: '600',
    },
    senderNameBold: {
        lineHeight: 20,
        marginLeft: 4,
    },
    timestamp: {
        fontSize: 11,
        color: 'foreground.weak',
        fontWeight: '400',
    },
    date: {
        color: colors.foreground.weak,
        fontWeight: '700',
    },
    time: {
        color: colors.foreground.weak,
    },
    message: {
        fontSize: 14,
        color: 'foreground.strong',
        lineHeight: 18,
        fontWeight: '400',
    },
    senderInfo: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
});
